// useStudentSearch — search engine for the student command palette.
//
// Responsibilities:
//   • Debounce keystrokes (avoid a request per character).
//   • Cancel stale in-flight requests with AbortController so results never
//     arrive out of order.
//   • Cache results per query (instant when the user backtracks).
//   • Filter the static navigation actions locally (no network, instant).
//
// Returns { query, setQuery, groups, actions, loading, error, reset } where
// `groups` matches the backend shape: { courses, assignments, quizzes, materials }.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../utils/apiClient';
import { STUDENT_ACTIONS } from '../data/commandPaletteActions';

const MIN_LEN = 2;
const DEBOUNCE_MS = 250;
const CACHE_MAX = 50;
const MAX_ACTIONS = 5;

export default function useStudentSearch() {
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  // ── Instant local filter over static navigation actions ──
  const actions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < MIN_LEN) return [];
    return STUDENT_ACTIONS.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.subtitle?.toLowerCase().includes(q) ||
        a.keywords?.some((k) => k.includes(q))
    ).slice(0, MAX_ACTIONS);
  }, [query]);

  const runSearch = useCallback(async (q) => {
    const key = q.toLowerCase();

    // Cache hit — serve immediately, skip the network.
    if (cacheRef.current.has(key)) {
      setGroups(cacheRef.current.get(key));
      setLoading(false);
      return;
    }

    // Cancel any in-flight request before starting a new one.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get('/student/search', {
        params: { q },
        signal: controller.signal,
      });
      const g = data?.groups || {};

      cacheRef.current.set(key, g);
      if (cacheRef.current.size > CACHE_MAX) {
        // Evict oldest (insertion-ordered Map) — simple LRU-ish bound.
        cacheRef.current.delete(cacheRef.current.keys().next().value);
      }

      // Ignore if a newer request superseded this one.
      if (abortRef.current === controller) setGroups(g);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
      setError(e);
      setGroups({});
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, []);

  // Debounced trigger on query change.
  useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length < MIN_LEN) {
      if (abortRef.current) abortRef.current.abort();
      setGroups({});
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true); // show spinner during the debounce window too
    debounceRef.current = setTimeout(() => runSearch(q), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery('');
    setGroups({});
    setError(null);
    setLoading(false);
  }, []);

  return { query, setQuery, groups, actions, loading, error, reset };
}
